<template>
  <div>
    <d-card>
      <v-form
        ref="form"
        :data="form"
        format="formData"
        :url="endpoint.url"
        :form-data-options="{
          nullsAsUndefineds: false,
          booleansAsIntegers: true,
          allowEmptyArrays: true,
          indices: true,
        }"
        :method="endpoint.method"
        @success="$router.push('/properties/'+propertyId+'/property-inspections')"
      >
        <d-card-row>
          <v-input-date
            v-model="form.date"
            required
            name="date"
          />
        </d-card-row>

        <d-card-row>
          <h3 class="mb-4 has-text-weight-semibold">
            {{ $t('property_inspection_elements') }}
          </h3>

          <div
            v-if="form.elements.length > 0"
            class="mb-4"
          >
            <table class="table table-element is-bordered is-striped">
              <thead>
                <th>
                  {{ $t('type') }}
                </th>
                <th>
                  {{ $t('description') }}
                </th>
                <th>
                  {{ $t("property_inspection_form_picture") }}
                </th>
                <th />
              </thead>
              <tbody>
                <tr
                  v-for="(el, key) in form.elements"
                  :key="key"
                >
                  <td>
                    {{ $t(el.type) }}
                  </td>
                  <td>
                    <div
                      class="content"
                      v-html="el.notes"
                    />
                  </td>
                  <td>
                    <div v-if="el.is_new && el.image">
                      <v-upload-item
                        v-for="(file, index) in el.image.to_add"
                        :key="index"
                        :file="file"
                        @delete="el.image.to_add.splice(index, 1)"
                      />
                    </div>
                    <div v-if="!el.is_new">
                      <v-media-show
                        v-for="(file) in el.media"
                        :key="file.id"
                        :file="file"
                      >
                        <template #preview="{url}">
                          <a
                            :href="url"
                            class="button is-small is-underline"
                            download
                          >
                            {{ $t('download').capitalize() }}
                          </a>
                        </template>
                      </v-media-show>
                    </div>
                  </td>
                  <td>
                    <a
                      type="button"
                      style="text-decoration: underline"
                      @click="removePropertyInspectionElement(key)"
                    >
                      {{ $t('remove') }}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <button
            class="button is-small"
            type="button"
            @click="togglePropertyInspectionModal"
          >
            <span class="icon">
              <i class="mdi mdi-plus" />
            </span>
            <span>
              {{ $t('add') }}
            </span>
          </button>

          <b-modal
            v-model="isShownPropertyInspectionElement"
            v-bind="form.elements"
            :width="600"
            @close="togglePropertyInspectionModal"
          >
            <div class="modal-card">
              <header
                class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
              >
                {{ $t("property_inspection_elements_modal_title").capitalize() }}
              </header>
              <section class="modal-card-body">
                <div class="mb-4">
                  <v-input-select
                    v-model="element.type"
                    required
                    name="type"
                  >
                    <option value="">
                      {{ $t("select").capitalize() }}
                    </option>
                    <option
                      v-for="option in $laravel.propertyInspectionElementTypes"
                      :key="option"
                      :value="option"
                    >
                      {{ $t(option).capitalize() }}
                    </option>
                  </v-input-select>
                </div>

                <div class="mb-4">
                  <v-input-rich-text
                    v-model="element.notes"
                    type="text"
                    name="notes"
                    :label="$t('description').capitalize()"
                    :has-info="false"
                  />
                </div>

                <div class="mb-4">
                  <p class="mb-2 mt-2">
                    {{ $t('property_inspection_form_picture') }}
                  </p>

                  <d-upload
                    :existing-files="[]"
                    @input="handleOnChangeElementImage"
                  />
                </div>
              </section>
              <footer class="modal-card-foot">
                <b-button
                  :label="$t('close')"
                  @click="togglePropertyInspectionModal"
                />
                <button
                  class="button is-primary"
                  type="button"
                  @click="addPropertyInspectionElement"
                >
                  {{ $t("add") }}
                </button>
              </footer>
            </div>
          </b-modal>
        </d-card-row>

        <d-card-row>
          <button
            class="button is-primary"
            type="submit"
          >
            {{ $t('save') }}
          </button>
        </d-card-row>
      </v-form>
    </d-card>
  </div>
</template>

<script>
import { fillFields } from "@/services/helpers";

import UploadPictures from "./components/UploadPictures.vue";
import MediaShow from "@/plugins/dashboard/MediaShow.vue";
import UploadPictureItem from "./components/UploadPictureItem";

export default {
  components: {
    "d-upload": UploadPictures,
    "v-media-show": MediaShow,
    "v-upload-item" : UploadPictureItem
  },
  props: {
    action: {
      type: String,
      required: true
    },
    model: {
      default: null,
      type: Object
    },
    propertyId: {
      required: true,
      type: Number,
    },
  },
  data () {
    return {
      form: {
        date: "",
        property_cleanliness: null,
        property_id: this.propertyId,
        elements: [],
      },
      isShownPropertyInspectionElement: false,
      element: {
        name : "test",
        image: null,
        approval_date: "",
        approved_by_client: false,
        notes: "",
        type:"",
      }
    };
  },
  computed: {
    endpoint () {
      if (this.action === "create") {
        return {
          method: "post",
          url: this.$laravel.route("api.property-inspections.store", {property: this.propertyId})
        };
      }
      if (this.action === "edit") {
        return {
          method: "patch",
          url: this.$laravel.route("api.property-inspections.update", {property: this.propertyId ,property_inspection: this.model.id})
        };
      }
      return null;
    },
  },
  created () {
    if (this.action === "edit") {
      this.form = fillFields(this.form, this.model);
    }
  },
  methods: {
    handleOnChangeElementImage(image) {
      this.element.image = image;
    },
    togglePropertyInspectionModal()
    {
      this.isShownPropertyInspectionElement = !this.isShownPropertyInspectionElement;

      this.element = {
        name : "",
        image: null,
        approval_date: "",
        approved_by_client: false,
        notes: "",
        type: this.element.type,
        is_new: true,
      };

    },
    addPropertyInspectionElement()
    {
      const element = {
        name: this.element.name,
        image: this.element.image,
        approval_date: this.element.approval_date,
        approved_by_client: this.element.approved_by_client,
        notes: this.element.notes,
        type: this.element.type,
        is_new: true,
      };

      this.form.elements.push(element);

      this.togglePropertyInspectionModal();
      this.element = {
        name : "",
        image: null,
        approval_date: "",
        approved_by_client: false,
        notes: "",
        type: this.element.type,
        is_new: true,
      };
    },
    removePropertyInspectionElement(index)
    {
      this.form.elements.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.table-element th {
  font-size: 14px;
}
.table-element td {
  font-size: 14px;
  border-bottom: solid 1px #ddd;
}
</style>
